import { makeObservable, observable, action, computed } from 'mobx'
import { get } from 'mobx'
import { BaseItem } from '../base/BaseItem'
import { Recurrence } from '../utils/recurrence'
import { Comments } from '../utils/comments'
import { History } from '../utils/history'
import { Subtasks } from '../utils/subtasks'

/**
 * Class representing a Sprint item, extending the base list item class LstItmBase.
 * Contains additional properties and methods specific to Sprint items.
 *
 * @class Sprint
 * @extends {BaseItem}
 */
export class Sprint extends BaseItem {
	_class = 'Sprint'
	sprint_id

	/**
	 * Creates an instance of Sprint.
	 * @param {string} id - The unique identifier for the Sprint item.
	 */
	constructor(id) {
		super(id)
		this.sprint_id = id
		this.subtasks = new Subtasks(this.full.subtasks || [])
		this.recurrence = new Recurrence(this.full.recurrence || {})
		this.comments = new Comments(this.full.comments || [])
		this.history = new History(this.full.history || {})

		// Pass the annotations to makeObservable
		makeObservable(this, {
			sprint_id: observable,
			subtasks: observable,
			recurrence: observable,
			comments: observable,
			history: observable,
			setRecurrence: action,
			setComments: action,
			updateFromServerCompact: action,
			updateFromServerFull: action,

			// Sprint specific methods
			priority: computed,
			setPriority: action.bound,

			effortEstimate: computed,
			setEffortEstimate: action.bound,

			platformIds: computed,
			setPlatformIds: action.bound,

			category_id: computed,
			setCategory_id: action.bound
		})
	}

	/**
	 * Updates the subtasks of the sprint.
	 * @param {Array} subtasks - Array of subtasks to be assigned to the sprint.
	 */
	setSubtasks = (subtasks) => {
		this.subtasks = new Subtasks(subtasks)
		this.chgInFull(['subtasks'], subtasks || [])
	}

	/**
	 * Updates the recurrence settings of the sprint.
	 * @param {object} recurrence - Recurrence object to assign.
	 */
	setRecurrence = (recurrence) => {
		this.recurrence = new Recurrence(recurrence)
		this.chgInFull(['recurrence'], recurrence)
	}

	/**
	 * Updates the comments associated with the sprint.
	 * @param {Array} comments - Array of comment objects.
	 */
	setComments = (comments) => {
		this.comments = new Comments(comments)
		this.chgInFull(['comments'], comments)
	}

	/**
	 *  Sprint specific methods
	 */
	// Priority field (0 is default, higher numbers indicate higher priority)
	get priority() {
		return get(this.full, ['priority'], get(this.compact, ['priority'], 0)) // Default priority to 0
	}

	/**
	 * Sets the priority of the sprint.
	 * @param {number} val - The priority value to set.
	 */
	setPriority = (val) => {
		this.chgInFull(['priority'], val)
	}

	// Effort estimate for the item
	get effortEstimate() {
		return get(
			this.full,
			['effortEstimate'],
			get(this.compact, ['effortEstimate'], 0)
		)
	}

	/**
	 * Sets the effort estimate for the sprint.
	 * @param {number} val - The estimated number of hours.
	 */
	setEffortEstimate = (val) => {
		this.chgInFull(['effortEstimate'], val)
	}

	/*
	 * @returns {Array} - The assigned platform ids.
	 * - platform id is in direct relation with user.platforms
	 */
	get platformIds() {
		return get(this.full, ['platformIds'], [])
	}

	/**
	 * Sets the platform IDs for the sprint.
	 * @param {Array} val - Array of platform ID strings.
	 */
	setPlatformIds = (val) => {
		this.chgInFull(['platformIds'], val)
	}

	/**
	 * Category id associated with the item.
	 * @returns {string} The category id.
	 */
	get category_id() {
		return get(
			this.full,
			['category_id'],
			get(this.compact, ['category_id'], '')
		)
	}

	/**
	 * Sets the category id.
	 * @param {string} val - The category id.
	 */
	setCategory_id = (val) => {
		this.chgInFull(['category_id'], val)
	}

	/**
	 * Computes and sets the next recurrence date based on the current recurrence settings.
	 * Updates sprint progress accordingly and resets subtasks if needed.
	 */
	setRecurrenceNext() {
		try {
			console.debug('[setRecurrenceNext] Computing next recurrence...')

			const onCompletion = () => {
				console.debug(
					'[setRecurrenceNext] No future recurrences. Marking as completed.'
				)
				this.setProgress('completed')
			}
			const onNext = () => {
				console.debug(
					'[setRecurrenceNext] Future recurrence found. Marking as active.'
				)
				this.setProgress('active')
			}

			this.recurrence.calculateNext(onCompletion, onNext)

			// After calculateNext, safely access and use .next
			if (this.recurrence.next && this.subtasks.list.length > 0) {
				console.debug('[setRecurrenceNext] Resetting subtasks...')
				this.subtasks.resetStatus()
			}

			// Avoid updating with invalid data
			if (typeof this.recurrence.next === 'number') {
				this.chgInFull(['recurrence', 'next'], this.recurrence.next)
			} else {
				console.warn(
					'[setRecurrenceNext] Skipped chgInFull due to invalid next date:',
					this.recurrence.next
				)
			}
		} catch (err) {
			console.error(
				'[setRecurrenceNext] Failed to update recurrence:',
				err
			)
		}
	}

	/**
	 * Override updateFromServerCompact to initialize values from server data.
	 * @param {object} compactJson - Compact data from the server.
	 */
	updateFromServerCompact(compactJson) {
		if (compactJson.recurrence) {
			this.recurrence = new Recurrence(compactJson.recurrence)
		}
		// Set Sprint specific properties (required due to the override)
		if (compactJson.priority) {
			this.setPriority(compactJson.priority)
		}
		if (compactJson.category_id) {
			this.setCategory_id(compactJson.category_id)
		}
	}

	/**
	 * Override updateFromServerFull to initialize values from server data.
	 * @param {object} fullJson - Full data from the server.
	 */
	updateFromServerFull(fullJson) {
		if (fullJson.recurrence) {
			this.recurrence = new Recurrence(fullJson.recurrence)
		}
		if (fullJson.comments) {
			this.comments = new Comments(fullJson.comments)
		}
		if (fullJson.history) {
			this.history = new History(fullJson.history)
		}
		if (fullJson.subtasks) {
			this.subtasks = new Subtasks(fullJson.subtasks)
		}

		// Set Sprint specific properties (required due to the override)
		if (fullJson.priority) {
			this.setPriority(fullJson.priority)
		}
		if (fullJson.category_id) {
			this.setCategory_id(fullJson.category_id)
		}

		if (fullJson.effortEstimate) {
			this.setEffortEstimate(fullJson.effortEstimate)
		}
		if (fullJson.platformIds) {
			this.setPlatformIds(fullJson.platformIds)
		}
	}
}
